/**
 * composition/bootstrap.ts
 *
 * Wires the composition resolver into the agent session lifecycle.
 *
 * This module provides:
 *   - `resolveAgentComposition()` — called by session-builder.ts to produce
 *     inline blocks (→ system prompt) and discoverable entries (→ capability
 *     registry)
 *   - `registerCompositionCapabilities()` — called by serve.ts to register
 *     discoverable entries as Protocol v2 capabilities
 *
 * Phase 1 limitation: the content loader uses `resolveMixin()` which only
 * resolves local-path refs (./file.md, /abs/path). Scoped catalog refs
 * (@publisher/name) require a populated RepoMap from workspace config, which
 * Phase 2 / Library integration will provide. Unresolvable refs produce
 * warnings but do not block session start.
 */

import { existsSync } from "node:fs";
import { homedir } from "node:os";
import { join } from "node:path";
import type { AgentManifest } from "@skaile/workspaces/core";
import type { IAssetIndex } from "@skaile/workspaces/library";
import type { Logger } from "@skaile/workspaces/types";
import * as z from "zod";
import type { CapabilityRegistry } from "../capability-registry.js";
import { defineCapability } from "../define-capability.js";
import { type RepoMap, resolveMixin } from "../mixin-resolver.js";
import { loadCompileManifestFromDir } from "./compile.js";
import { resolveComposition } from "./resolve.js";
import type { DiscoverableEntry, ResolvedComposition } from "./types.js";

// Re-export for downstream convenience
export type { ResolvedComposition };

// ---------------------------------------------------------------------------
// Options
// ---------------------------------------------------------------------------

/**
 * Options for {@link resolveAgentComposition}.
 *
 * @docLink packages/runner/capabilities#resolve-agent-composition
 */
export interface ResolveAgentCompositionOpts {
  /** Path to the agent definition directory (where agent.yaml lives) */
  agentDir: string;
  /** Parsed agent manifest (must have `composes` field) */
  manifest: AgentManifest;
  /**
   * Project root — reserved for Phase 2 workspace config resolution.
   * In Phase 1 this is unused but accepted so the call site doesn't
   * need updating when Library integration lands.
   */
  projectDir: string;
  /** Pre-built repo map for catalog resolution. Empty in Phase 1. */
  repos?: RepoMap;
  /** Logger for composition resolution warnings */
  log?: Logger;
}

// ---------------------------------------------------------------------------
// Public API: Resolve
// ---------------------------------------------------------------------------

/**
 * Resolve an agent's `composes:` items into inline blocks and discoverable
 * entries. Called by session-builder.ts during the session creation pipeline.
 *
 * Returns null when the manifest has no `composes:` items.
 *
 * @param opts - Resolution options including agentDir, manifest, and optional repos/log.
 * @returns A {@link ResolvedComposition} or `null` when there are no composition items.
 * @docLink packages/runner/capabilities#resolve-agent-composition
 */
export async function resolveAgentComposition(
  opts: ResolveAgentCompositionOpts,
): Promise<ResolvedComposition | null> {
  const { agentDir, manifest, repos = {}, log } = opts;

  const items = manifest.composes;
  if (!items || items.length === 0) return null;

  // Load compile manifest for inline-snapshot verification
  const compileManifest = await loadCompileManifestFromDir(agentDir);

  // Build a content loader using the existing mixin resolver.
  // Phase 1: only local-path refs resolve successfully. Scoped catalog refs
  // (@publisher/name) require a populated RepoMap which Phase 2 provides.
  // Non-matching kinds simply return null (surfaces as a compose warning).
  const loadContent = async (ref: string, kind: string): Promise<string | null> => {
    try {
      const resolved = await resolveMixin(ref, kind, agentDir, repos);
      return resolved?.content ?? null;
    } catch {
      return null;
    }
  };

  // Try to open the real LocalIndex for asset description enrichment.
  // Falls back to a null-returning stub when the library DB doesn't exist
  // (user hasn't run `skaile source add` + `skaile source sync` yet).
  const library = await openLocalIndexIfAvailable(log);

  const result = await resolveComposition({
    items,
    library,
    compileManifest,
    loadContent,
  });

  // Log warnings so they surface in session debug logs
  if (log && result.warnings.length > 0) {
    for (const warning of result.warnings) {
      log.warn(warning);
    }
  }

  return result;
}

// ---------------------------------------------------------------------------
// Public API: Register Capabilities
// ---------------------------------------------------------------------------

/**
 * Register discoverable composition entries as Protocol v2 capabilities.
 *
 * Each discoverable entry becomes a tool the LLM can invoke to "activate"
 * that asset. When invoked, the handler returns the asset's full content
 * (e.g. a SKILL.md body for skills, connection instructions for connectors).
 *
 * The LLM sees a lean tool list (name + description only) and loads full
 * content on demand — this keeps the context window efficient for agents
 * that compose many assets.
 *
 * @param registry - The session capability registry to register capabilities onto.
 * @param entries - Discoverable entries from a prior {@link resolveAgentComposition} call.
 * @docLink packages/runner/capabilities#register-composition-capabilities
 */
export function registerCompositionCapabilities(
  registry: CapabilityRegistry,
  entries: DiscoverableEntry[],
): void {
  for (const entry of entries) {
    const cap = defineCapability({
      name: entry.name,
      displayName: `${entry.kind}: ${entry.ref}`,
      description: entry.description,
      side: "agent",
      origin: { kind: "skill", skillId: entry.ref },
      scope: "session",
      kind: "query",
      userInvokable: false,
      input: z.strictObject({}),
      handler: async (_args, ctx) => {
        ctx.log.info("loading composed asset content", {
          ref: entry.ref,
          kind: entry.kind,
        });
        return { content: entry.content, ref: entry.ref, kind: entry.kind };
      },
    });
    registry.register(cap, "agent");
  }
}

// ---------------------------------------------------------------------------
// Internal: Library resolution
// ---------------------------------------------------------------------------

/**
 * Null-returning IAssetIndex stub used when no real Library is available.
 * All query methods return empty; write methods are no-ops.
 */
const STUB_LIBRARY: IAssetIndex = {
  getAssetDef: async () => null,
  listAssetDefs: async () => [],
  createInstance: async () => ({}) as any,
  getInstance: async () => null,
  listInstances: async () => [],
  updateInstance: async () => ({}) as any,
  deleteInstance: async () => {},
  assign: async () => ({}) as any,
  unassign: async () => {},
  listAssignments: async () => [],
  getConsumptionGraph: async () => [],
};

/**
 * Try to open the LocalIndex SQLite store if it exists.
 * Returns a stub when the library DB is missing (user hasn't synced yet)
 * or when opening fails (e.g. corrupt DB).
 */
async function openLocalIndexIfAvailable(log?: Logger): Promise<IAssetIndex> {
  // Probe for the index file at the canonical 2026-05-27 location plus the
  // two legacy ones (lib.db inside libraries/ or library/). Migration to the
  // canonical path is handled by `createLibraryDb`; here we only need to know
  // whether *some* index file is present before constructing LocalIndex.
  const indexPath = process.env.SKAILE_INDEX_PATH ?? join(homedir(), ".skaile", "index.db");
  const librariesDir = process.env.SKAILE_LIBRARIES_DIR ?? join(homedir(), ".skaile", "libraries");
  const legacyHome = join(homedir(), ".skaile", "library");
  const probes = [
    indexPath,
    join(librariesDir, "index.db"),
    join(librariesDir, "lib.db"),
    join(legacyHome, "lib.db"),
  ];

  if (!probes.some((p) => existsSync(p))) {
    return STUB_LIBRARY;
  }

  try {
    // Deferred import so the runner does not eagerly pull in the library
    // subpath when no local library DB exists.
    const { LocalIndex } = await import("@skaile/workspaces/library");
    return new LocalIndex() as IAssetIndex;
  } catch (err) {
    log?.debug(`Could not open LocalIndex at ${indexPath}: ${(err as Error).message}`);
    return STUB_LIBRARY;
  }
}
